import type { Comic, ComicCategory, Author, NewsItem } from "../types/comic";
import { comics } from "../mocks/comics";
import { comicCategories } from "../mocks/categories";
import { authors } from "../mocks/authors";
import { newsList } from "../mocks/news";

export const getComicById = (id?: string): Comic | undefined =>
  comics.find((c) => c.id === id);

export const getCategoryById = (id?: string): ComicCategory | undefined =>
  comicCategories.find((c) => c.id === id);

export const getAuthorById = (id?: string): Author | undefined =>
  authors.find((a) => a.id === id);

export const getNewsById = (id?: string): NewsItem | undefined =>
  newsList.find((n) => n.id === id);

export const getComicsByCategory = (categoryId: string): Comic[] =>
  comics.filter((c) => c.categoryIds.includes(categoryId));

export const getComicsByAuthor = (authorId: string): Comic[] =>
  comics.filter((c) => c.authorId === authorId);

export const getRelatedComics = (comic: Comic, limit = 4): Comic[] => {
  const scored = comics
    .filter((c) => c.id !== comic.id)
    .map((c) => ({
      comic: c,
      score:
        c.categoryIds.filter((id) => comic.categoryIds.includes(id)).length * 10 +
        (c.authorId === comic.authorId ? 3 : 0) +
        c.popularity / 100,
    }))
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.comic);
};

export const getPopularComics = (limit = 6): Comic[] =>
  [...comics].sort((a, b) => b.popularity - a.popularity).slice(0, limit);

export const getLatestUpdatedComics = (limit = 8): Comic[] =>
  [...comics].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit);

export const getFeaturedComics = (): Comic[] => comics.filter((c) => c.featured);

export const searchComics = (keyword: string): Comic[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return comics.filter((c) => {
    const haystack = [
      c.title,
      c.authorName,
      c.description,
      c.tags.join(" "),
      c.categoryIds
        .map((id) => getCategoryById(id)?.name ?? "")
        .join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const searchNews = (keyword: string): NewsItem[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return newsList.filter((n) =>
    [n.title, n.summary, n.category, n.source].join(" ").toLowerCase().includes(q)
  );
};

export const getSortedNews = (): NewsItem[] =>
  [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1));

export const getCategoryName = (id: string): string =>
  getCategoryById(id)?.name ?? "";

export const formatDate = (date: string): string => {
  const [y, m, d] = date.split("-");
  return `${y} 年 ${Number(m)} 月 ${Number(d)} 日`;
};